import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable, computed, signal } from '@angular/core';

/** Body returned by the APIs (MaintenanceModeMiddleware) when maintenance mode is enabled. */
interface MaintenanceResponse {
  maintenance: true;
  message?: string;
}

export const defaultMaintenanceMessage = 'We are currently carrying out scheduled maintenance. Please try again shortly.';

@Injectable({
  providedIn: 'root',
})
export class MaintenanceService {
  private readonly maintenanceMessage = signal<string | null>(null);

  /** True once any API has responded with a maintenance response. */
  public readonly isInMaintenance = computed(() => this.maintenanceMessage() !== null);
  public readonly message = computed(() => this.maintenanceMessage() ?? defaultMaintenanceMessage);

  public static isMaintenanceResponse(error: unknown): error is HttpErrorResponse & { error: MaintenanceResponse } {
    return (
      error instanceof HttpErrorResponse &&
      error.status === HttpStatusCode.ServiceUnavailable &&
      (error.error as Partial<MaintenanceResponse> | null)?.maintenance === true
    );
  }

  public enterMaintenance(message?: string | null) {
    this.maintenanceMessage.set(message?.trim() || defaultMaintenanceMessage);
  }

  /**
   * For use during app initialisation: swallows the error if an API reported maintenance mode (so the app can still
   * boot and show the maintenance page), otherwise rethrows.
   */
  public ignoreErrorIfInMaintenance(error: unknown): void {
    if (MaintenanceService.isMaintenanceResponse(error)) {
      this.enterMaintenance(error.error.message);
    }

    if (!this.isInMaintenance()) {
      throw error;
    }
  }
}
